import * as React from 'react';
import { Navigate } from 'react-router-dom';
import { ArticleType, PublicAmorgUser, SimpleArticleList, stringUtil, SubscriberArticlePage, SubscriberArticleSort, UserToken } from '../../../amorg-common';
import { getAuthorizedActiveUserOrRedirect, getMaybeActiveUserOrRefresh, redirectToErrorMessage } from '../../../api/serverApiHelpers';
import { getArticleListing, getArticleListTopics, getArticleTypes, getCurrentArticleOwners } from '../../../api/siteArticlesClientApi';
import { FullUserAvatar } from '../../../designSystem/avatar/UserAvatar';
import { ImportanceAvatar } from '../../../designSystem/avatar/ImportanceAvatar';
import { UserAvatar } from '../../../designSystem/avatar/UserAvatar';
import { Button } from '../../../designSystem/button/Button';
import { CardPager } from '../../../designSystem/compositeComponents/CardPager';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { FormField } from '../../../designSystem/formField/FormField';
import { SelectField } from '../../../designSystem/formField/SelectField';
import { Icon, iconName } from '../../../designSystem/icons/Icons';
import { TextInput } from '../../../designSystem/inputs/TextInput';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../../designSystem/PageHeader';
import { Table, TableHeaderIcon, TableHeaderIconWithSort, TableHeaderWithSort, TableRowWithOnClick } from '../../../designSystem/Table';
import { H2 } from '../../../designSystem/typography/Heading';
import { P } from '../../../designSystem/typography/Body';
import { amorgLocalStorage, ClientArticlePageFilter, getDefaultClientArticlePageFilter } from '../../../utils/amorgLocalStorage';
import { clientAccessHelper } from '../../../utils/clientAccessHelper';
import { toDateString } from '../../../utils/date';
import { articleIconHelper } from '../../../utils/iconHelper';
import { ProtectedPageContext } from '../../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../../_pageWrappers/MemberPageWrapper';

export function ArticleListPage (): JSX.Element
{
    const listIdQueryParm = new URLSearchParams(window.location.search).get('lid') || '';
    const [listId, setListId] = React.useState<string>(listIdQueryParm || '');
    const [listTopics, setListTopics] = React.useState<SimpleArticleList[]>([]);

    const list = listId ? listTopics.find(l => l.id === listId) : undefined;
    const listInfoName = listId ? (list ? list.name : '') : 'Alle lovkrav';

    return (
        <MemberPageWrapper
            selectedMenuItemKey={'articles'}
            breadCrumbs={[
                { text: 'Lovservice', url: '/articles' },
                ...(listInfoName ? [{ text: listInfoName, url: '' }] : [])
            ]}>
            <PageHeader
                headerIcon='balance'
                headerText='Lovservice' />
            <SingleColumnLayout>
                <ArticleListPageContent
                    listId={listId}
                    listTopics={listTopics}
                    onListIdChanged={setListId}
                    onListTopicsChanged={setListTopics} />
            </SingleColumnLayout>
        </MemberPageWrapper>
    );
}

type ArticleListPageContentProps = {
    listId:string,
    listTopics:SimpleArticleList[],
    onListIdChanged:(listId:string) => void,
    onListTopicsChanged:(listTopics:SimpleArticleList[]) => void,
}

function ArticleListPageContent (props:ArticleListPageContentProps): JSX.Element
{
    const listIdQueryParm = new URLSearchParams(window.location.search).get('lid') || '';
    const searchTermQueryParm = new URLSearchParams(window.location.search).get('search') || '';

    const [loading, setLoading] = React.useState(true);

    const [activeUser, setActiveUser] = React.useState<UserToken | null>(null);

    const [types, setTypes] = React.useState<ArticleType[]>([]);
    const [currentOwners, setCurrentOwners] = React.useState<PublicAmorgUser[]>([]);
    
    const [searchText, setSearchText] = React.useState(searchTermQueryParm || '');
    const [filter, setFilter] = React.useState<ClientArticlePageFilter>(getDefaultClientArticlePageFilter());    

    const [listing, setListing] = React.useState<SubscriberArticlePage | null>(null);

    const [redirect, setRedirect] = React.useState('');

    React.useEffect(() =>
    {
        props.onListIdChanged(listIdQueryParm);
        setSearchText(searchTermQueryParm);
        getData(listIdQueryParm, searchTermQueryParm);
    }, [listIdQueryParm, searchTermQueryParm]);

    async function getData(newListId:string, newSearchTerm:string)
    {
        // Force refresh if needed to prevent empty lists triggering "no access" error on users with expired tokens
        await getMaybeActiveUserOrRefresh();

        setLoading(true);

        setActiveUser(await getAuthorizedActiveUserOrRedirect([]));
        
        const currentOwners = await getCurrentArticleOwners();
        setCurrentOwners(currentOwners);

        const listTopics = await getArticleListTopics();
        if(listTopics.length === 0)
            redirectToErrorMessage('Der opstod en fejl ved indlæsning af lovkravsemner. Prøv at genindlæse siden, og kontakt support hvis problemet fortsætter.');
        props.onListTopicsChanged(listTopics);

        const types = await getArticleTypes();
        setTypes(types);

        const newFilter = amorgLocalStorage.articles.readFilter(
            newListId,
            listTopics,
            types,
            currentOwners
        );

        setFilter(newFilter);

        const listing = await getArticleListing(newListId, newFilter.topicId, newFilter.typeId, newFilter.ownerUserId, newFilter.page, newSearchTerm, newFilter.sort);
        setListing(listing);
        setLoading(false);
    }


    async function applyFilter(newListId:string, newSearchTerm:string, filter: ClientArticlePageFilter)
    {
        setLoading(true);

        props.onListIdChanged(newListId);
        setSearchText(newSearchTerm);
        const newListing = await getArticleListing(newListId, filter.topicId, filter.typeId, filter.ownerUserId, filter.page, newSearchTerm, filter.sort);
        setFilter(filter);
        amorgLocalStorage.articles.saveFilter(newListId, filter);
        setListing(newListing);
        
        setLoading(false);
    }

    async function handleSelectListId (newListId: string)
    {
        // Must apply the existing filter to the new list filter
        props.onListIdChanged(newListId);
        applyFilter(newListId, searchText, { ...filter, page: 1 });
    }

    async function handleSelectTopic (topicId: string)
    {
        applyFilter(props.listId, searchText, { ...filter, topicId, page: 1 });
    }

    async function handleSelectType (typeId: string)
    {
        applyFilter(props.listId, searchText, { ...filter, typeId, page: 1 });
    }

    async function handleSelectOwner (ownerUserId: string)
    {
        if(filter.ownerUserId === ownerUserId || ownerUserId === '')
            applyFilter(props.listId, searchText, { ...filter, ownerUserId: '', page: 1 });
        else
            applyFilter(props.listId, searchText, { ...filter, ownerUserId, page: 1 });
    }

    async function handleSearch ()
    {
        applyFilter(props.listId, searchText, { ...filter, page: 1 });
    }

    async function handleResetFilter()
    {
        applyFilter(props.listId, '', getDefaultClientArticlePageFilter());
    }

    async function handleSortChange (sortBy: SubscriberArticleSort, ascending: boolean)
    {
        applyFilter(props.listId, searchText, { ...filter, sort: { sortBy, ascending } });
    }

    async function handlePageChange (newPage: number)
    {
        applyFilter(props.listId, searchText, { ...filter, page: newPage });
    }

    let listInfo: { name: string, icon: iconName; } = { name: '', icon: 'help' };
    if (props.listId) 
    { 
        if(props.listTopics.length > 0)
        {
            const list = props.listTopics.find(l => l.id === props.listId);
            if (list)
            {
                listInfo = { name: list.name, icon: articleIconHelper.getArticleListIcon(list.name) };
            }
        }
    }
    else {
        listInfo = { name: 'Alle lovkrav', icon: 'balance' };
    }

    const showReference = activeUser?.userId === 'sysadmin';

    const contextUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(contextUser, ['ViewCustomFormAnswerList']);

    return (
        <Flex flexDirection='column' gap={32}>

                {redirect &&
                    <Navigate to={redirect} />
                }

                <LoadingPageOverlay loading={loading} />

                {!hasAccess
                    ? <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>
                    :
                    <>
                    <Flex gap={8} alignItems='center'>
                        <Icon name={listInfo.icon} />
                        <H2>{listInfo.name}</H2>
                    </Flex>

                    <Flex flexDirection="column" gap={16}>

                        <Flex gap={16} alignItems='flex-end'>

                            <SelectField
                                label='Filtrér efter liste'
                                minWidth="200px"
                                emptyText='Alle lister'
                                options={props.listTopics.map(l => ({ value: l.id, text: l.name }))}
                                selectedValue={props.listId}
                                onChange={handleSelectListId}
                            />

                            <SelectField
                                label='Filtrér efter emne'
                                minWidth="340px"
                                emptyText='Alle emner'
                                options={
                                    props.listId 
                                        ? (props.listTopics.find(l => l.id === props.listId)?.topics.map(t => ({ value: t.id, text: stringUtil.capTextLength(t.name, 40) })) || [])
                                        : props.listTopics.flatMap(l => l.topics.map(t => ({ value: t.id, text: stringUtil.capTextLength(t.name, 40) })))
                                }
                                selectedValue={filter.topicId}
                                onChange={handleSelectTopic}
                            />

                            <SelectField
                                label='Filtrér efter type'
                                emptyText='Alle typer'
                                options={types.map(t => ({ value: t.id, text: t.name }))}
                                selectedValue={filter.typeId}
                                onChange={handleSelectType}
                            />

                            <FormField
                                label='Søgetekst'
                                inputComponent={
                                    <Flex gap={4} alignItems='center'>
                                        <TextInput trailingIcon='interface_actions_search'
                                            value={searchText}
                                            onChange={(value) => setSearchText(value)}
                                            onKeyDown={(e) =>
                                            {
                                                if (e.key === 'Enter')
                                                    handleSearch();
                                            }} />

                                        <Button variant='text' text='Søg' onClick={() => { 
                                            handleSearch();
                                        }} />

                                        <Button variant='text' icon='actions_close' text='Ryd filter' onClick={() => { 
                                            handleResetFilter();
                                        }} />
                                    </Flex>
                                }
                            />
                        </Flex>

                        {currentOwners.length > 0 &&
                            <FormField
                                label='Filtrér efter ansvarlig'
                                inputComponent={
                                    <Flex gap={8}>
                                        {currentOwners.map(owner => 
                                            <FullUserAvatar
                                                key={owner.id}
                                                user={{...owner}}
                                                selected={filter.ownerUserId === owner.id}
                                                onClick={handleSelectOwner}
                                            />
                                        )}
                                    </Flex>
                                }
                            />
                        }

                        <section>
                            <Table>
                                <thead>
                                    <tr>
                                        <TableHeaderWithSort
                                            text=""
                                            isActiveSort={filter.sort.sortBy === 'actionStatus'}
                                            ascending={filter.sort.ascending}
                                            onClick={(ascending) => handleSortChange('actionStatus', ascending)}
                                        />
                                        {showReference &&
                                            <th>
                                                lovnummer
                                            </th>
                                        }
                                        <TableHeaderWithSort
                                            text="Emne"
                                            isActiveSort={filter.sort.sortBy === 'topic'}
                                            ascending={filter.sort.ascending}
                                            onClick={(ascending) => handleSortChange('topic', ascending)}
                                        />
                                        <TableHeaderWithSort
                                            text="Type"
                                            isActiveSort={filter.sort.sortBy === 'type'}
                                            ascending={filter.sort.ascending}
                                            onClick={(ascending) => handleSortChange('type', ascending)}
                                        />
                                        <TableHeaderWithSort
                                            text="Lovkrav"
                                            isActiveSort={filter.sort.sortBy === 'title'}
                                            ascending={filter.sort.ascending}
                                            onClick={(ascending) => handleSortChange('title', ascending)}
                                        />
                                        <TableHeaderIconWithSort
                                            icon='calendar'
                                            tooltipContent="Dato for seneste opdatering, eller oprettelse af lovkravet i lovservice"
                                            isActiveSort={filter.sort.sortBy === 'releaseDate'}
                                            ascending={filter.sort.ascending}
                                            onClick={(ascending) => handleSortChange('releaseDate', ascending)}
                                            width="140px"
                                        />
                                        <TableHeaderIcon
                                            icon="stacks"
                                            tooltipContent='Seneste version'
                                            width="80px"
                                        />
                                        <TableHeaderIcon icon="interface_user_user" tooltipContent='Ansvarlig' />
                                        <TableHeaderIconWithSort
                                            icon='balance'
                                            tooltipContent="Praksis for juridisk forpligtelse"
                                            isActiveSort={filter.sort.sortBy === 'requirementsStatus'}
                                            ascending={filter.sort.ascending}
                                            width="80px"
                                            onClick={(ascending) => handleSortChange('requirementsStatus', ascending)}
                                        />
                                        <TableHeaderIconWithSort
                                            icon="priority"
                                            tooltipContent="Creceas estimerede betydning af ændringen"
                                            isActiveSort={filter.sort.sortBy === 'importance'}
                                            ascending={filter.sort.ascending}
                                            width="80px"
                                            onClick={(ascending) => handleSortChange('importance', ascending)}
                                        />
                                        <TableHeaderIconWithSort
                                            icon="thumbs_up_down"
                                            tooltipContent="Din vurdering af ændringen"
                                            isActiveSort={filter.sort.sortBy === 'evaluationImportance'}
                                            ascending={filter.sort.ascending}
                                            width="80px"
                                            onClick={(ascending) => handleSortChange('evaluationImportance', ascending)}
                                        />
                                        <TableHeaderIconWithSort
                                            icon="fact_check"
                                            tooltipContent="Antal åbne handlingsplaner knyttet til lovkravet"
                                            isActiveSort={filter.sort.sortBy === 'openActionPlans'}
                                            ascending={filter.sort.ascending}
                                            width="80px"
                                            onClick={(ascending) => handleSortChange('openActionPlans', ascending)}
                                        />
                                    </tr>
                                </thead>
                                <tbody>
                                    {listing && listing.articles.map(article => 
                                    {
                                        return (
                                            <TableRowWithOnClick key={article.articleId} onClick={() => setRedirect(`/articles/${article.articleId}`)}>
                                                <td>
                                                    <Flex alignItems="center" justifyContent="center">
                                                        {article.action === '0 - requirements missing' &&
                                                            <Icon name='warning' color='orange' toolTipText='Praksis for juridisk forpligtelse bør udfyldes' />
                                                        }
                                                        {article.action === '1 - evaluation missing' &&
                                                            <Icon name='warning' color='orange' toolTipText='Evaluering bør foretages' />
                                                        }
                                                        {article.action === '2 - evaluation recommended' &&
                                                            <Icon name='warning' color='orange' toolTipText='Evaluering bør muligvis foretages' />
                                                        }
                                                    </Flex>
                                                </td>
                                                {showReference &&
                                                    <td>
                                                        {article.reference ? `#${article.reference}` : ''}
                                                    </td>
                                                }
                                                <td>{article.topic.name}</td>
                                                <td>{article.type.name}</td>
                                                <td>{article.title}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {article.release?.releasedDate
                                                        ? toDateString(article.release.releasedDate)
                                                        : ''}
                                                </td>
                                                <td>{article.version}</td>
                                                <td>
                                                    {article.owner 
                                                        ?   <UserAvatar
                                                                user={{
                                                                    displayName: article.owner.displayName,
                                                                    initials: article.owner.initials
                                                                }}
                                                                identifier={article.owner.userId}
                                                                size="24px"
                                                            />
                                                        :   undefined
                                                    }
                                                </td>
                                                <td>
                                                    {article.requirementsStatus === '0 - missing' &&
                                                        <Icon name='status_empty' color='gray' toolTipText='Praksis for juridisk forpligtelse er ikke udfyldt' />
                                                    }
                                                    {article.requirementsStatus === '1 - defined' &&
                                                        <Icon name='status_ok' color='green' toolTipText='Praksis for juridisk forpligtelse er udfyldt' />
                                                    }
                                                </td>
                                                <td>
                                                    <ImportanceAvatar importance={article.publication.importance} />
                                                </td>
                                                <td>
                                                    <ImportanceAvatar mode="evaluation" importance={article.evaluationImportance || undefined} />
                                                </td>
                                                <td>
                                                    {article.openActionPlanCount}
                                                </td>
                                            </TableRowWithOnClick>
                                        );
                                    })}
                                </tbody>
                            </Table>

                            <CardPager
                                currentPage={listing?.page || 1}
                                items={listing?.totalItems || 0}
                                itemsPerPage={listing?.itemsPerPage || 20}
                                onPageChange={handlePageChange}
                            />

                        </section>

                    </Flex>
                    </>
                }
            </Flex>
    );
}